import { useLocation } from 'react-router-dom';
import { useAppContext } from '@/context/AppContext';
import { DateRangePicker } from '@/components/composites/DateRangePicker';
import { noop } from '@/lib/noop';
import { currentDate } from '@/lib/dates';
import { resolvePreset } from '@/lib/dateRangePresets';
import type { DateRangePreset } from '@/types/daterange';
import styles from './TopBar.module.css';

// Minimal path → breadcrumb-title lookup. Prefix match; longer keys win.
const PATH_TITLES: ReadonlyArray<readonly [string, string]> = [
  ['/dashboard', 'Dashboard'],
  ['/team', 'Team Stats'],
  ['/analytics/domains', 'Domains'],
  ['/reports/applications', 'Applications'],
  ['/reports/monthly', 'Monthly Report'],
  ['/reports/grouped', 'Grouped Report'],
  ['/reports/work-log-review', 'Work Log Review'],
  ['/timesheet/range', 'Timesheet · Range'],
  ['/timesheet/daily', 'Timesheet · Daily'],
  ['/categorize', 'Categorize Time'],
  ['/clients', 'Clients & Projects'],
  ['/projects', 'Projects'],
  ['/rules', 'Rules'],
  ['/platform/categories/edit', 'Platform — Taxonomy Editor'],
  ['/platform/categories/promotion-status', 'Platform — Promotion Status'],
  ['/platform/categories', 'Platform — Taxonomy'],
  ['/platform/hosts', 'Platform — Hosts'],
  ['/platform/tenants', 'Platform — Tenants'],
  ['/settings/organization', 'Settings · Organization'],
  ['/settings/team', 'Settings · Team'],
  ['/settings/access', 'Settings · Access'],
  ['/settings/collectors', 'Settings · Collectors'],
  ['/settings/integrations/jira', 'Settings · Jira integration'],
  ['/settings/account', 'Settings · Account'],
  ['/settings', 'Settings'],
  ['/employees', 'Team Stats'],
];

function breadcrumbTitle(pathname: string): string {
  for (const [prefix, title] of PATH_TITLES) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return title;
  }
  return 'Kanjoy';
}

// Relative presets the picker exposes as static ranges; if the emitted
// start/end matches any of these, tag the stored range with the preset key
// so the next mount re-resolves it against a current clock instead of
// showing a stale literal window.
const RELATIVE_PRESETS_TO_DETECT: ReadonlyArray<DateRangePreset> = [
  'today',
  'thisWeek',
  'thisMonth',
  'last7',
  'last30',
];

function detectPreset(start: string, end: string): DateRangePreset {
  const now = currentDate();
  for (const preset of RELATIVE_PRESETS_TO_DETECT) {
    const r = resolvePreset(preset, now);
    if (r.start === start && r.end === end) return preset;
  }
  return 'custom';
}

export function TopBar() {
  const { dateRange, setDateRange, sidebarCollapsed } = useAppContext();
  const location = useLocation();
  const title = breadcrumbTitle(location.pathname);

  const handleRangeChange = (start: string, end: string) => {
    setDateRange({ start, end, preset: detectPreset(start, end) });
  };

  return (
    <header role="banner" className={`${styles.topbar} ${sidebarCollapsed ? styles.shifted : ''}`}>
      <div className={styles.left}>
        <nav className={styles.breadcrumb} aria-label="Breadcrumb">
          <span className={styles.breadcrumbCurrent}>{title}</span>
        </nav>
      </div>
      <div className={styles.right}>
        <DateRangePicker
          startDate={dateRange.start}
          endDate={dateRange.end}
          onRangeChange={handleRangeChange}
        />
        <button type="button" className={styles.iconBtn} aria-label="Notifications" onClick={noop}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M13.5 6a4.5 4.5 0 0 0-9 0c0 5.25-2.25 6.75-2.25 6.75h13.5S13.5 11.25 13.5 6z" />
            <path d="M10.3 15.75a1.5 1.5 0 0 1-2.6 0" />
          </svg>
          <span className={styles.notifDot} data-testid="notif-count" aria-hidden="true" />
        </button>
      </div>
    </header>
  );
}
